import {
  DocumentStatus as DocumentStatusEnum,
  FILE_UPLOAD_STATUS_VALUES,
  FileUploadStatus as FileUploadStatusEnum,
  PRE_CHECK_STATUS_VALUES,
  PRE_CHECK_WARNING_CODES,
  PreCheckStatus as PreCheckStatusEnum,
} from '../../payload/enums/index'
import { getId } from '../../payload/payload.helpers'
import { PayloadDocument } from '../../payload/payload-types.zod'
import z from 'zod'

export const DocumentStatus = DocumentStatusEnum

/**
 * Statuses meaning the AI pipeline still has work to do on this document.
 *
 * `draft` is deliberately absent: a draft is never committed to the pipeline, so one the user
 * abandoned would otherwise count as in-flight forever. `done` and `error` are both terminal —
 * a failed document must not hold anything back.
 *
 * The web app keeps a parallel list in `useProcessingDocuments` for the regen offer, with the
 * same reasoning and the same stuck bound.
 */
const PROCESSING_STATUSES: DocumentDb['status'][] = [
  DocumentStatusEnum.WAITING_FOR_UPLOAD,
  DocumentStatusEnum.UPLOADED,
  DocumentStatusEnum.PARSING,
  DocumentStatusEnum.ANALYZING,
  DocumentStatusEnum.EMBEDDING,
]

// A document that hasn't moved in this long is assumed abandoned — a failed upload stuck at
// `waiting_for_upload`, or a worker that died before writing a terminal status. Without the
// bound, one such document would suppress anything gated on "is the pipeline idle" forever,
// because nothing will ever write to it again.
const DOCUMENT_STUCK_MS = 30 * 60 * 1000

/** Whether the pipeline still owes work on this document — see PROCESSING_STATUSES. */
export function isDocumentProcessing(document: {
  status: DocumentDb['status']
  createdAt?: string | null
  updatedAt?: string | null
}): boolean {
  if (!PROCESSING_STATUSES.includes(document.status)) return false

  const movedAt = Date.parse(document.updatedAt ?? document.createdAt ?? '')

  return Number.isNaN(movedAt) || movedAt > Date.now() - DOCUMENT_STUCK_MS
}

export const FileUploadStatus = FileUploadStatusEnum
export const PreCheckStatus = PreCheckStatusEnum

export const DocumentFileSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  originalName: z.string(),
  filePath: z.string(),
  mimeType: z.string(),
  bytes: z.number(),
  order: z.number(),
  checksum: z.string().nullish(),
  uploadStatus: z.enum(FILE_UPLOAD_STATUS_VALUES as [string, ...string[]]),
})

// Permissive: matches the regenerated PayloadDocument exactly so DocumentDbSchema
// can extend it without narrowing collisions. The tight typed shape (with
// PreCheckWarningCode[] warnings) lives on PreChecksResponseSchema below and is
// applied only at the API response boundary.
export const PreChecksSchema = z.object({
  status: z.enum(PRE_CHECK_STATUS_VALUES as [string, ...string[]]).optional(),
  results: z.any().optional(),
})

// Tight, typed shape used in API responses. Frontend reads typed warnings.
export const PreCheckResultsSchema = z.object({
  warnings: z.array(z.enum(PRE_CHECK_WARNING_CODES as [string, ...string[]])),
})
export const PreChecksResponseSchema = z.object({
  status: z.enum(PRE_CHECK_STATUS_VALUES as [string, ...string[]]).optional(),
  results: PreCheckResultsSchema.nullish(),
})

// PayloadDocument is regenerated from payload-types.ts; until regen, the new
// fields (files, combinedFilePath, preChecks), relaxed required fields
// (filePath, bytes, mimeType), and the new DRAFT status live in this overlay.
export const DocumentDbSchema = PayloadDocument.extend({
  filePath: z.string().optional(),
  mimeType: z.string().optional(),
  bytes: z.number().optional(),
  status: z.enum([
    'draft',
    'analyzing',
    'done',
    'embedding',
    'error',
    'parsing',
    'uploaded',
    'waiting_for_upload',
  ]),
  files: z.array(DocumentFileSchema).optional(),
  combinedFilePath: z.string().optional(),
  preChecks: PreChecksSchema.optional(),
})

export const CreateDocumentFileSchema = z.object({
  originalName: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(100),
  bytes: z.number().min(1),
})

export const CreateDocumentSchema = z.object({
  name: z.string().min(1).max(255),
  errand: z.string().min(1).optional(),
  files: z.array(CreateDocumentFileSchema).min(1),
})

export const CreateDocumentArraySchema = z.array(CreateDocumentSchema).min(1)

export const AddFilesToDocumentSchema = z.object({
  files: z.array(CreateDocumentFileSchema).min(1),
})

export const MergeDocumentsSchema = z.object({
  targetDocumentId: z.string().min(1),
  sourceDocumentIds: z.array(z.string().min(1)).min(1),
})

export const SplitDocumentSchema = z.object({
  fileIds: z.array(z.string().min(1)).min(1),
})

export const ReorderFilesSchema = z.object({
  files: z.array(z.object({ fileId: z.string().min(1), order: z.number() })).min(1),
})

export const RenameFileSchema = z.object({
  name: z.string().min(1).max(255),
})

export const RenameDocumentSchema = z.object({
  name: z.string().min(1).max(255),
})

export const UploadedFileInfoSchema = z.object({
  fileId: z.string(),
  originalName: z.string(),
  filePath: z.string(),
})

export const DocumentUploadResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  files: z.array(UploadedFileInfoSchema),
})

export const DocumentUploadResponseArraySchema = z.array(DocumentUploadResponseSchema)

export const DocumentFileResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  originalName: z.string(),
  filePath: z.string(),
  mimeType: z.string(),
  bytes: z.number(),
  order: z.number(),
  uploaded: z.boolean(),
})

export const DocumentResponseSchema = DocumentDbSchema.omit({
  bytes: true,
  checksum: true,
  filePath: true,
  files: true,
  combinedFilePath: true,
  preChecks: true,
  individual: true,
  summaries: true,
  errand: true,
}).extend({
  files: z.array(DocumentFileResponseSchema),
  // Path to the merged PDF when the combine phase produced one. Exposed so the
  // documents-table preview can open the combined PDF instead of the first file.
  combinedFilePath: z.string().optional(),
  // Document-level pre-AI checks. Advisory: the pipeline doesn't block on them.
  // Exposed so the upload page can render a status icon per row and the combine
  // wizard can render the pre-analyze results panel. Uses the tight typed
  // response schema (with PreCheckWarningCode[] warnings) so the generated
  // frontend client gets the typed shape, not `any`.
  preChecks: PreChecksResponseSchema.optional(),
  summaries: z.object({
    brief: z.string().optional(),
    comprehensive: z.string().optional(),
    headline: z.string().optional(),
    simple: z.string().optional(),
  }),
  errands: z.array(z.string()).optional(),
})

export const DocumentResponseArraySchema = z.array(DocumentResponseSchema)

export type CreateDocument = z.infer<typeof CreateDocumentSchema>
export type CreateDocumentArray = z.infer<typeof CreateDocumentArraySchema>
export type CreateDocumentFile = z.infer<typeof CreateDocumentFileSchema>
export type AddFilesToDocument = z.infer<typeof AddFilesToDocumentSchema>
export type MergeDocuments = z.infer<typeof MergeDocumentsSchema>
export type SplitDocument = z.infer<typeof SplitDocumentSchema>
export type ReorderFiles = z.infer<typeof ReorderFilesSchema>
export type RenameFile = z.infer<typeof RenameFileSchema>
export type RenameDocument = z.infer<typeof RenameDocumentSchema>
export type DocumentDb = z.infer<typeof DocumentDbSchema>
export type DocumentFile = z.infer<typeof DocumentFileSchema>
export type DocumentResponse = z.infer<typeof DocumentResponseSchema>
export type DocumentResponseArray = z.infer<typeof DocumentResponseArraySchema>
export type DocumentUploadResponse = z.infer<typeof DocumentUploadResponseSchema>
export type DocumentUploadResponseArray = z.infer<typeof DocumentUploadResponseArraySchema>
export type DocumentFileResponse = z.infer<typeof DocumentFileResponseSchema>

const synthesizeLegacyFiles = (data: DocumentDb): DocumentFile[] => {
  // Defensive fallback: legacy Documents predate the files[] array.
  // The migration backfills these, so this branch should be dead post-migration.
  if (!data.filePath || !data.mimeType || data.bytes == null) return []
  const storageName = data.filePath.split('/').pop() ?? data.name
  return [
    {
      name: storageName,
      originalName: data.name,
      filePath: data.filePath,
      mimeType: data.mimeType,
      bytes: data.bytes,
      order: 0,
      checksum: data.checksum ?? undefined,
      uploadStatus: FileUploadStatus.UPLOADED,
    },
  ]
}

export const getDocumentFiles = (data: DocumentDb): DocumentFile[] => {
  const files = data.files && data.files.length > 0 ? data.files : synthesizeLegacyFiles(data)
  return [...files].sort((a, b) => a.order - b.order)
}

export const documentDbToResponse = (data: DocumentDb): DocumentResponse => {
  const files = getDocumentFiles(data)
  return DocumentResponseSchema.parse({
    activities: data.activities ?? [],
    categories: data.categories ?? [],
    categoryTags: data.categoryTags || [],
    createdAt: data.createdAt,
    diagnoses: data.diagnoses ?? [],
    files: files.map((f, idx) => ({
      id: f.id ?? `${data.id}-${idx}`,
      name: f.name,
      originalName: f.originalName,
      filePath: f.filePath,
      mimeType: f.mimeType,
      bytes: f.bytes,
      order: f.order,
      uploaded: f.uploadStatus === FileUploadStatus.UPLOADED,
    })),
    combinedFilePath: data.combinedFilePath,
    preChecks: data.preChecks,
    id: data.id,
    instance: data.instance?.trim(),
    issuanceAt: data.issuanceAt,
    issuers: data.issuers ?? [],
    name: data.name.trim(),
    status: data.status,
    summaries: {
      brief: data.summaries?.brief?.result,
      comprehensive: data.summaries?.comprehensive?.result,
      headline: data.summaries?.headline?.result,
      simple: data.summaries?.simple?.result,
    },
    type: data.type,
    updatedAt: data.updatedAt,
    userTags: data.userTags,
    errands: data.errand ? data.errand.docs?.map(getId) : [],
  } satisfies DocumentResponse)
}

/**
 * The date we treat as a document's point in time: the user-supplied
 * `issuanceAt` when present, otherwise the record's `createdAt`. Both are ISO
 * strings. `issuanceAt` is optional and not auto-set, hence the fallback.
 */
export const getDocumentEffectiveDate = (doc: {
  issuanceAt?: string | null
  createdAt?: string | null
}): string | undefined => doc.issuanceAt ?? doc.createdAt ?? undefined

/**
 * Sorts documents newest-first by effective date (see {@link getDocumentEffectiveDate}).
 * Documents without a usable date sort last while keeping their original
 * relative order. Returns a new array; the input is not mutated.
 *
 * Generic over the document shape so it works for both `DocumentResponse` and
 * `DocumentDb` (they only need to expose the two date fields).
 */
export function sortDocumentsByDate<
  T extends { issuanceAt?: string | null; createdAt?: string | null },
>(docs: T[]): T[] {
  return docs
    .map((doc, index) => {
      const date = getDocumentEffectiveDate(doc)
      const time = date ? new Date(date).getTime() : Number.NaN
      return { doc, index, time }
    })
    .sort((a, b) => {
      const aValid = !Number.isNaN(a.time)
      const bValid = !Number.isNaN(b.time)
      if (aValid && bValid) return b.time - a.time
      if (aValid) return -1
      if (bValid) return 1
      return a.index - b.index
    })
    .map(entry => entry.doc)
}

/**
 * Builds the per-document context fed to recommendation/description prompts.
 * Documents are sorted newest-first and each line is prefixed with its effective
 * date so the model can weigh recency and resolve temporal conflicts (e.g. an
 * old "currently in school" statement vs. a recent "daglig verksamhet" one).
 *
 * Returns both the `sorted` documents and the `formattedLines` (one per document
 * that has a comprehensive summary) so callers can join or reuse them as they see
 * fit — e.g. the recommendations prompt joins the lines, while the individual
 * description prompt keeps its own richer layout but reuses the ordering.
 */
export function formatDocumentsForPrompt(docs: DocumentResponse[]): {
  sorted: DocumentResponse[]
  formattedLines: string[]
} {
  const sorted = sortDocumentsByDate(docs)
  const formattedLines = sorted
    .filter(doc => doc.summaries.comprehensive)
    .map(doc => {
      const date = getDocumentEffectiveDate(doc)
      const dateLabel = date ? date.slice(0, 10) : 'Okänt datum'
      return `**Utfärdad:** ${dateLabel}\n${doc.summaries.comprehensive}`
    })
  return { sorted, formattedLines }
}
